import { Component, EventEmitter, Input, Output } from '@angular/core';
import { LayoutName } from 'emma-common-ts/emma';

import Chart from 'chart.js';
import { EMMAChartBase } from './emma-chart-base';

declare const mUtil: any;

export enum TotalType {
  LAST,
  SUM,
}

export interface Data {
  labels: string[];
  values: { [k: string]: number };
  variation?: number;
  error?: Error;
  loading?: boolean;
}

@Component({
    selector: 'emma-minichart',
    templateUrl: './emma-chart.template.html',
    standalone: false
})
export class EMMAMiniChartComponent extends EMMAChartBase {
  @Input()
  total_type!: string;
  @Input()
  override layout: LayoutName = 'portlet-minichart';
  @Output()
  override chartLoad = new EventEmitter<boolean>();
  @Output()
  override exported = new EventEmitter<string>();

  totalType: TotalType = TotalType.SUM;

  override init() {
    if (this.total_type && this.total_type.toLocaleLowerCase() === 'last') {
      this.totalType = TotalType.LAST;
    } else {
      this.totalType = TotalType.SUM;
    }

    if (!window.document.getElementById(this.chartId)) {
      return;
    }

    const el = document.getElementById(this.chartId) as HTMLCanvasElement;
    if (!el) {
      throw new Error('Chart not found: ' + this.chartId);
    }
    const ctx = el.getContext('2d');
    if (!ctx) {
      throw new Error('Chart without Context2D: ' + this.chartId);
    }

    const config: any = {
      type: 'line',
      data: {
        labels: [],
        datasets: [],
      },
      options: {
        title: {
          display: false,
        },
        tooltips: {
          enabled: false,
        },
        legend: {
          display: false,
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              display: false,
              gridLines: false,
            },
          ],
          yAxes: [
            {
              display: false,
              gridLines: false,
            },
          ],
        },
        elements: {
          line: {
            tension: 0.0000001,
          },
          point: {
            radius: 4,
            borderWidth: 12,
          },
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 5,
            bottom: 0,
          },
        },
      },
    };

    this.chart = new Chart(ctx, config);
    this.chartLoad.emit(true);
    if (this.export) {
      this.export.subscribe(() => this.exportChart());
    }
  }

  override update(data: Data | null) {
    if (data && data.error) {
      return this.error(data.error);
    } else if (!data || data.loading) {
      return this.loading();
    }
    const { labels, values } = data;
    this.loaded();
    const borderColorMinichart = this.color;
    const colorMinichart = mUtil.getColor(borderColorMinichart);

    const el = document.getElementById(this.chartId) as HTMLCanvasElement;
    const ctx = el && el.getContext('2d');
    if (!ctx) {
      return;
    }
    const gradient = ctx.createLinearGradient(0, 0, 0, 240);
    gradient.addColorStop(0, Chart.helpers.color(colorMinichart).alpha(0.7).rgbString());
    gradient.addColorStop(1, Chart.helpers.color(colorMinichart).alpha(0).rgbString());

    const dataset = {
      label: this.chartTitle,
      backgroundColor: gradient, // Put the gradient here as a fill color
      borderColor: colorMinichart,

      pointBackgroundColor: 'rgba(255,255,255,0)',
      pointBorderColor: 'rgba(255,255,255,0)',
      pointHoverBackgroundColor: Chart.helpers.color('#0a2c33').alpha(0.8).rgbString(),
      pointHoverBorderColor: Chart.helpers.color('#0a2c33').alpha(0.2).rgbString(),

      // fill: 'start',
      data: labels.map((label) => values[label] || 0),
    };

    this.chart.data.labels = labels;
    this.chart.data.datasets = [dataset];
    this.chart.update();
  }
}
